import { Checkbox, List, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import React from 'react'

import Drawer, { IProps as DrawerProps } from '@/atoms/Drawer/Drawer'
import { LoaderOverlay } from '@/atoms/Loader/Loader'
import { useShowModal } from '@/atoms/ModalApi'
import WizardPage, { WizardButton } from '@/atoms/WizardPage/WizardPage'
import { FEATURE_FLAGS, getStorageValue, setStorageValue, STORAGE_KEYS, storages } from '@/lib/browserStorage'
import { useLoadingSelector, useStoreValue } from '@/lib/react-hooks/generic-hooks'
import Wizard from '@/molecules/Wizard/Wizard'
import { MyCompanyActions, MyCompanyTypes } from '@/redux/actions'
import { setNewHotelUIEnabled } from '@/redux/actions/new-hotel-experience.actions'
import { useDispatch } from '@/redux/stores'
import { CompanyFeatureFlagsZ } from '@/types/company'

import { SncfConfirmation } from './SncfConfirmation'

export type FeatureFlagSidebarProps = Omit<DrawerProps, 'ref'> & {
	className?: string
}

const storageKey = STORAGE_KEYS.COMPANY_FEATURE_FLAG_OVERRIDES
const enableSncfFlag = FEATURE_FLAGS.ENABLE_SNCF_PER_COMPANY
const newHotelExperienceFlag = FEATURE_FLAGS.ENABLE_NEW_HOTEL_EXPERIENCE

const createFeatureFlagPayload = (flagKey: typeof enableSncfFlag, newValue: boolean) => ({
	feature_flag: {
		[flagKey]: newValue,
	},
})

const createFlagToggleSuccessCallback =
	(flagKey: string, setFlagValue: (key: string) => void, closeModal: () => void) => () => {
		window.location.reload()
		setFlagValue(flagKey)
		closeModal()
	}

export const FeatureFlagSidebar = (props: FeatureFlagSidebarProps) => {
	const { className, onClose, ...rest } = props
	const dispatch = useDispatch()
	const showModal = useShowModal()
	const isLoading = useLoadingSelector(MyCompanyTypes.GetMyCompany)
	const company = useStoreValue((s) => s.myCompany)
	const featureFlags = company?.feature_flag as Record<string, boolean> | undefined
	const getCurrentValue = (key: string) => Boolean(featureFlags?.[key])

	const setFlagValue = (key: string) => {
		const current = getCurrentValue(key)
		const updatedOverrides = {
			...getStorageValue<Record<string, boolean>>(storages.local, storageKey),
			[key]: !current,
		}
		setStorageValue(storages.local, storageKey, updatedOverrides)
		dispatch({
			type: MyCompanyTypes.GetMyCompany.SUCCESS,
			payload: {
				...company,
				feature_flag: {
					...company?.feature_flag,
					...updatedOverrides,
				},
			},
		})
	}

	const handleSncfToggle = (key: typeof enableSncfFlag) => {
		const current = getCurrentValue(key)
		const newValue = !current

		showModal(({ close }) => (
			<SncfConfirmation
				close={close}
				success={() => {
					const payload = createFeatureFlagPayload(key, newValue)
					const successCallback = createFlagToggleSuccessCallback(key, setFlagValue, close)
					dispatch(MyCompanyActions.updateMyCompanyFeatureFlags(payload, successCallback))
				}}
				isEnable={newValue}
			/>
		))
	}

	const handleToggle = (key: string) => () => {
		if (!company) {
			return
		}

		if (key === enableSncfFlag) {
			return handleSncfToggle(key)
		}

		setFlagValue(key)
		if (key === newHotelExperienceFlag) {
			dispatch(setNewHotelUIEnabled(false))
		}
	}

	return (
		<Drawer className={className} open {...rest}>
			<Wizard>
				<WizardPage
					title="Company Feature Flags"
					proceedButtonProps={{ label: 'Close' }}
					onProceed={onClose}
					secondaryButton={
						<WizardButton
							color="negativeTransparent"
							onClick={() => {
								setStorageValue(storages.local, storageKey, {})
								dispatch(MyCompanyActions.getMyCompany())
								dispatch(setNewHotelUIEnabled(false))
							}}
							disabled={isLoading}
						>
							{'Reset'}
						</WizardButton>
					}
				>
					{isLoading && <LoaderOverlay />}
					<List>
						{Object.keys(CompanyFeatureFlagsZ.shape)
							.toSorted()
							.map((featureFlagId) => {
								const labelId = `ff-${featureFlagId}`
								return (
									<ListItem key={featureFlagId} role={undefined} dense onClick={handleToggle(featureFlagId)}>
										<ListItemIcon>
											<Checkbox
												edge="start"
												color="primary"
												checked={getCurrentValue(featureFlagId)}
												tabIndex={-1}
												inputProps={{ 'aria-labelledby': labelId }}
											/>
										</ListItemIcon>
										<ListItemText id={labelId} primary={featureFlagId} />
									</ListItem>
								)
							})}
					</List>
				</WizardPage>
			</Wizard>
		</Drawer>
	)
}
